import React, { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { CARE_TYPES, DAYS } from "@/lib/constants";

const EMPTY = { type: "feeding", title: "", frequency: "daily", times: ["08:00"], days_of_week: [], day_of_month: 1, interval_days: 30, start_date: "", amount: "", instructions: "" };

export function ScheduleDialog({ open, onOpenChange, petId, schedule, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [newTime, setNewTime] = useState("18:00");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(schedule ? { ...EMPTY, ...schedule } : EMPTY); }, [open, schedule]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...f, pet_id: petId, title: f.title || CARE_TYPES[f.type].label, start_date: f.start_date || null,
      day_of_month: Number(f.day_of_month) || 1, interval_days: Number(f.interval_days) || 1 };
    try {
      if (schedule?.id) await api.put(`/schedules/${schedule.id}`, body);
      else await api.post("/schedules", body);
      toast.success("Schedule saved");
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-3xl bg-[#FAF7F2]">
        <DialogHeader><DialogTitle className="font-heading text-2xl">{schedule ? "Edit care task" : "Add care task"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4" data-testid="schedule-form">
          <div className="flex flex-wrap gap-2">
            {Object.entries(CARE_TYPES).map(([k, t]) => (
              <button type="button" key={k} data-testid={`schedule-type-${k}`} onClick={() => set("type", k)}
                className={`pl-chip !py-2 border ${f.type === k ? "border-[#1E293B] bg-white" : "border-transparent"}`} style={{ background: f.type === k ? "#fff" : t.bg, color: t.color }}>
                <t.icon className="h-3.5 w-3.5" />{t.label}
              </button>
            ))}
          </div>
          <div><label className="pl-label">What is it?</label>
            <input data-testid="schedule-title-input" className="pl-input" placeholder={f.type === "medication" ? "e.g. Insulin" : "e.g. Breakfast"} value={f.title} onChange={(e) => set("title", e.target.value)} /></div>
          <div><label className="pl-label">Amount / details (as directed by your vet)</label>
            <input data-testid="schedule-amount-input" className="pl-input" placeholder="e.g. 1 cup kibble, or 4 units" value={f.amount || ""} onChange={(e) => set("amount", e.target.value)} /></div>
          <div><label className="pl-label">How often?</label>
            <select data-testid="schedule-frequency-select" className="pl-input" value={f.frequency} onChange={(e) => set("frequency", e.target.value)}>
              <option value="daily">Every day</option><option value="weekly">Certain weekdays</option>
              <option value="monthly">Once a month</option><option value="interval">Every N days</option>
            </select></div>
          {f.frequency === "weekly" && (
            <div className="flex gap-1.5 flex-wrap">{DAYS.map((d, i) => (
              <button type="button" key={d} data-testid={`schedule-day-${i}`} onClick={() => set("days_of_week", f.days_of_week.includes(i) ? f.days_of_week.filter((x) => x !== i) : [...f.days_of_week, i])}
                className={`h-11 w-12 rounded-xl font-semibold text-sm ${f.days_of_week.includes(i) ? "bg-[#F59E0B]" : "bg-white border"}`}>{d}</button>))}
            </div>
          )}
          {f.frequency === "monthly" && <div><label className="pl-label">Day of month</label><input type="number" min="1" max="28" className="pl-input" data-testid="schedule-dom-input" value={f.day_of_month} onChange={(e) => set("day_of_month", e.target.value)} /></div>}
          {f.frequency === "interval" && (
            <div className="grid grid-cols-2 gap-3">
              <div><label className="pl-label">Every (days)</label><input type="number" min="1" className="pl-input" data-testid="schedule-interval-input" value={f.interval_days} onChange={(e) => set("interval_days", e.target.value)} /></div>
              <div><label className="pl-label">Starting</label><input type="date" className="pl-input" data-testid="schedule-start-input" value={f.start_date || ""} onChange={(e) => set("start_date", e.target.value)} /></div>
            </div>
          )}
          <div><label className="pl-label">Times (leave empty for "anytime")</label>
            <div className="flex flex-wrap gap-2 mb-2">{f.times.map((t) => (
              <span key={t} className="pl-chip bg-white border !py-2 text-sm">{t}<button type="button" data-testid={`schedule-remove-time-${t}`} onClick={() => set("times", f.times.filter((x) => x !== t))}><X className="h-3.5 w-3.5" /></button></span>))}
            </div>
            <div className="flex gap-2"><input type="time" className="pl-input" data-testid="schedule-time-input" value={newTime} onChange={(e) => setNewTime(e.target.value)} />
              <button type="button" data-testid="schedule-add-time-btn" className="pl-btn-ghost shrink-0" onClick={() => newTime && !f.times.includes(newTime) && set("times", [...f.times, newTime].sort())}><Plus className="h-4 w-4" />Add</button></div>
          </div>
          <div><label className="pl-label">Instructions for helpers</label>
            <textarea data-testid="schedule-instructions-input" rows={3} className="pl-input py-3" placeholder="e.g. Give with food. Hide pill in cheese." value={f.instructions || ""} onChange={(e) => set("instructions", e.target.value)} /></div>
          {f.type === "medication" && <p className="text-xs text-slate-500">Enter exactly what your vet prescribed. PackLog never recommends doses. If a dose is missed, please contact your veterinarian.</p>}
          <button data-testid="schedule-save-btn" disabled={saving} className="pl-btn-primary w-full">{saving ? "Saving..." : "Save"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
